/**
 * BedConfigEditor — Sprint 31
 * Editor inline de configuració de llits per reserva.
 * S'integra al ReservationPopover com a secció col·lapsable.
 *
 * Disseny:
 * - Mostra icones actuals (BedIcons) + badge mascota
 * - Toggle "✏️ Editar" per expandir l'editor
 * - +/- per cada tipus de llit (matrimoni, individual, cuna, supletori)
 * - Toggle mascota (hasPet)
 * - "Restaurar defecte" → envia bedConfig: null (usa defaultBedConfig de la unit)
 * - "Desar" → crida PATCH /reservations/:id/bed-config
 * - Runbook 10: el selector és `BedPicker`; «Confirmar» (sense editar) → POST confirm-bed-config
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { BedIcons, type BedType } from './BedIcon';
import { BedPicker } from './BedPicker';
import { BED_AUTO_TEXT, bedWarningText, bedsToItems, type BedConfigSource, type BedWarning } from './bed-review';
import {
  useBedConfigMutation,
  useConfirmBedConfigMutation,
  type BedConfigItem,
} from '@/hooks/useBedConfigMutation';

// ============================================
// TYPES
// ============================================

interface BedConfigEditorProps {
  reservationId: string;
  /** Llits resolts actuals (ja expandits: ['double', 'single', 'crib']) */
  currentBeds: BedType[];
  /** Si la reserva té override propi (bedConfig !== null) */
  hasOverride: boolean;
  /** Si té mascota */
  hasPet: boolean;
  /** Runbook 10 — qui ha posat els llits ('auto' = preferència del client) */
  bedConfigSource?: BedConfigSource | null;
  /** Runbook 10 — avís calculat pel backend */
  bedWarning?: BedWarning | null;
  /** Callback quan es desa correctament */
  onSaved?: () => void;
}

// ============================================
// COMPONENT
// ============================================

export function BedConfigEditor({
  reservationId,
  currentBeds,
  hasOverride,
  hasPet,
  bedConfigSource = null,
  bedWarning = null,
  onSaved,
}: BedConfigEditorProps) {
  const [isEditing, setIsEditing] = useState(false);

  // Estat local de l'editor (inicialitzat des dels llits actuals)
  const [editItems, setEditItems] = useState<BedConfigItem[]>(() => bedsToItems(currentBeds));
  const [editHasPet, setEditHasPet] = useState(hasPet);

  const { mutate, isPending: isSaving } = useBedConfigMutation();
  const { mutate: confirm, isPending: isConfirming } = useConfirmBedConfigMutation();
  const isPending = isSaving || isConfirming;

  const isAuto = bedConfigSource === 'auto';
  const warningText = bedWarningText(bedWarning);
  // «Confirmar» fixa els llits tal com són: té sentit si són automàtics, hi ha avís o no n'hi ha de propis.
  const canConfirm = isAuto || warningText != null || !hasOverride;

  // Sincronitza l'estat local quan s'obre l'editor
  const handleToggleEdit = () => {
    if (!isEditing) {
      // Reinicialitza des dels valors actuals
      setEditItems(bedsToItems(currentBeds));
      setEditHasPet(hasPet);
    }
    setIsEditing((prev) => !prev);
  };

  const handleSave = () => {
    mutate(
      {
        reservationId,
        payload: {
          bedConfig: editItems.length > 0 ? editItems : null,
          hasPet: editHasPet,
        },
      },
      {
        onSuccess: () => {
          setIsEditing(false);
          onSaved?.();
        },
      },
    );
  };

  const handleRestoreDefault = () => {
    mutate(
      {
        reservationId,
        payload: { bedConfig: null, hasPet: editHasPet },
      },
      {
        onSuccess: () => {
          setIsEditing(false);
          onSaved?.();
        },
      },
    );
  };

  const handleConfirm = () => {
    confirm(reservationId, { onSuccess: () => onSaved?.() });
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Header: llits actuals + botó editar */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          {currentBeds.length > 0 ? (
            <BedIcons
              beds={currentBeds}
              gap="sm"
              className={cn(isAuto && 'border border-dashed border-gray-400 rounded px-0.5')}
            />
          ) : (
            <span className="text-[10px] text-gray-400 italic">Sense config</span>
          )}
          {hasPet && (
            <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1 py-0.5 rounded font-medium">
              🐕
            </span>
          )}
          {hasOverride && !isAuto && (
            <span
              className="text-[9px] text-blue-500 border border-blue-200 px-1 py-0.5 rounded"
              title="Config personalitzada per aquesta reserva"
            >
              ✏️
            </span>
          )}
        </div>
        <button
          className={cn(
            'text-[10px] font-medium px-1.5 py-0.5 rounded border transition-colors flex-shrink-0',
            isEditing
              ? 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
              : 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100',
          )}
          onClick={handleToggleEdit}
          disabled={isPending}
        >
          {isEditing ? '✕ Cancel·lar' : '✏️ Editar llits'}
        </button>
      </div>

      {/* Runbook 10 — avís, marca automàtica i «Confirmar» */}
      {(warningText || isAuto) && (
        <div className="flex flex-col gap-0.5">
          {warningText && (
            <p className="text-[10px] text-amber-700 leading-snug break-words">⚠ {warningText}</p>
          )}
          {isAuto && (
            <p className="text-[10px] text-gray-500 italic leading-snug break-words">{BED_AUTO_TEXT}</p>
          )}
        </div>
      )}
      {canConfirm && !isEditing && (
        <Button
          size="sm"
          variant="outline"
          className="self-start text-xs h-7 px-2 border-green-200 text-green-700 hover:bg-green-50"
          onClick={handleConfirm}
          disabled={isPending}
          title="Deixar els llits tal com són i marcar-los com a revisats"
        >
          {isConfirming ? 'Confirmant...' : '✔ Confirmar'}
        </Button>
      )}

      {/* Editor expandit */}
      {isEditing && (
        <div className="flex flex-col gap-2 bg-gray-50 rounded-lg p-2 border border-gray-200">
          <BedPicker value={editItems} onChange={setEditItems} disabled={isPending} />

          {/* Toggle mascota */}
          <div className="flex items-center justify-between pt-1 border-t border-gray-200">
            <span className="text-xs text-gray-700">🐕 Mascota</span>
            <button
              className={cn(
                'text-[10px] font-medium px-2 py-0.5 rounded border transition-colors',
                editHasPet
                  ? 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200'
                  : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200',
              )}
              onClick={() => setEditHasPet((prev) => !prev)}
              disabled={isPending}
            >
              {editHasPet ? '✅ Sí' : '❌ No'}
            </button>
          </div>

          {/* Botons d'acció */}
          <div className="flex gap-1.5 pt-1 border-t border-gray-200">
            <Button
              size="sm"
              variant="default"
              className="flex-1 text-xs h-7"
              onClick={handleSave}
              disabled={isPending}
            >
              {isPending ? 'Desant...' : '💾 Desar'}
            </Button>
            {hasOverride && (
              <Button
                size="sm"
                variant="outline"
                className="text-xs h-7 px-2 border-orange-200 text-orange-600 hover:bg-orange-50"
                onClick={handleRestoreDefault}
                disabled={isPending}
                title="Restaurar la configuració per defecte de l'habitació"
              >
                ↩️ Defecte
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default BedConfigEditor;
